import type React from 'react';
import { Button } from './button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from './dialog';

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  body: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'danger' | 'primary';
  isLoading?: boolean;
  size?: React.ComponentProps<typeof Dialog>['size'];
}

/**
 * Generic confirmation dialog. Use for any "are you sure?" interaction
 * before a destructive or irreversible action.
 *
 * Pass `confirmVariant="danger"` for destructive actions to render the
 * confirm button in stoplight-danger styling.
 */
export function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  isLoading = false,
  size = 'md',
}: ConfirmationDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} size={size}>
      <DialogTitle className="tw:!text-2xl">{title}</DialogTitle>
      <DialogBody>
        {typeof body === 'string' ? (
          <p className="tw:text-zinc-700 tw:dark:text-zinc-300">{body}</p>
        ) : (
          body
        )}
      </DialogBody>
      <DialogActions>
        <Button type="button" color="light" onClick={onClose} disabled={isLoading}>
          {cancelText}
        </Button>
        <Button
          type="button"
          color={confirmVariant === 'danger' ? 'stoplight-danger' : 'brandgreen0'}
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
